import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Send,
  MessageSquare,
  Loader2,
  User,
  Bot,
  Calendar,
  Clock,
  Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const industryInfo = {
  hvac: { name: 'HVAC & Heating', icon: '🔥', color: 'from-orange-500 to-red-600' },
  plumbing: { name: 'Plumbing', icon: '💧', color: 'from-blue-500 to-cyan-600' },
  electrical: { name: 'Electrical', icon: '⚡', color: 'from-yellow-500 to-amber-600' },
  home_repair: { name: 'Home Repair', icon: '🔧', color: 'from-slate-500 to-zinc-600' },
  dental: { name: 'Dental & Medical', icon: '🦷', color: 'from-teal-500 to-emerald-600' },
  roofing: { name: 'Roofing', icon: '🏠', color: 'from-stone-500 to-slate-600' },
  landscaping: { name: 'Landscaping', icon: '🌲', color: 'from-green-500 to-emerald-600' },
  auto_repair: { name: 'Auto Repair', icon: '🚗', color: 'from-red-500 to-rose-600' },
  septic: { name: 'Septic Services', icon: '🚽', color: 'from-amber-600 to-yellow-700' },
  restaurant: { name: 'Restaurant', icon: '🍽️', color: 'from-amber-500 to-orange-600' },
  fishing_charter: { name: 'Fishing Charter', icon: '🎣', color: 'from-blue-600 to-indigo-600' },
  sightseeing: { name: 'Sightseeing Tours', icon: '🏔️', color: 'from-sky-500 to-blue-600' },
  gym: { name: 'Gym & Fitness', icon: '💪', color: 'from-purple-500 to-violet-600' },
  real_estate: { name: 'Real Estate', icon: '🏢', color: 'from-emerald-500 to-teal-600' }
};

export default function ChatDemo() {
  const { industry } = useParams();
  const navigate = useNavigate();
  
  // Setup state
  const [companyName, setCompanyName] = useState('');
  const [demoStarted, setDemoStarted] = useState(false);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [scheduledAppointment, setScheduledAppointment] = useState(null);
  const messagesEndRef = useRef(null);
  
  const info = industryInfo[industry] || industryInfo.hvac;

  // Start demo with greeting
  const startDemo = () => {
    if (!companyName.trim()) return;
    
    setDemoStarted(true);
    const greeting = {
      role: 'assistant',
      content: `Hello! Thank you for calling ${companyName}. I'm your AI assistant. How can I help you today?\n\nI can help you with:\n• Scheduling appointments\n• Emergency service requests\n• Pricing inquiries\n• General questions about our services`
    };
    setMessages([greeting]);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/demo/voice-chat`, {
        industry: industry,
        message: input,
        conversation_history: [...messages, userMessage].slice(-6),
        company_name: companyName
      });

      const aiMessage = { role: 'assistant', content: response.data.response };
      setMessages(prev => [...prev, aiMessage]);
      
      // Check if appointment was scheduled
      if (response.data.appointment) {
        setScheduledAppointment(response.data.appointment);
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: "I'm sorry, I'm having trouble connecting right now. Please try again." 
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Industry-specific quick replies
  const quickReplies = industry === 'real_estate' 
    ? [
        "I want to sell my home",
        "I'm looking to buy",
        "What's my home worth?",
        "Schedule a showing"
      ]
    : [
        "I have an emergency!",
        "Schedule an appointment",
        "What times are available?",
        "How much do you charge?"
      ];

  // Pre-demo setup screen
  if (!demoStarted) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        {/* Header */}
        <header className="border-b border-slate-800 bg-slate-900/50">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/')}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-lg">Chat Demo</span>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            {/* Industry Badge */}
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${info.color} mb-6`}>
              <span className="text-xl">{info.icon}</span>
              <span className="font-medium">{info.name}</span>
            </div>

            <h1 className="text-3xl font-bold mb-4">Try Our AI Chat Assistant</h1>
            <p className="text-slate-400 mb-8">
              Enter your company name to see how our AI would handle customer chats for YOUR business.
            </p>
            
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 max-w-md mx-auto">
              <label className="block text-left text-sm text-slate-400 mb-2">Your Company Name</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder={`e.g., Smith's ${info.name}`}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-sky-500 mb-6"
                data-testid="chat-company-name-input"
                onKeyDown={(e) => e.key === 'Enter' && startDemo()}
              />
              
              <Button
                onClick={startDemo}
                disabled={!companyName.trim()}
                className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="start-chat-demo-btn"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Start Chat Demo
              </Button>
              
              <p className="text-slate-500 text-xs mt-4">
                This demo shows how our AI would handle customer inquiries for your {info.name.toLowerCase()} business.
              </p>
            </div>

            {/* What This Demo Shows */}
            <div className="mt-8 p-4 bg-slate-800/30 rounded-xl border border-slate-700/50 max-w-md mx-auto">
              <h3 className="text-sm font-medium text-sky-400 mb-2">What this demo shows:</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                This AI acts as YOUR receptionist, handling chat inquiries in your company's name. 
                It can assess urgency and schedule appointments using mock availability 
                (simulating a calendar integration like Calendly).
              </p>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  // Active chat demo
  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => {
                setDemoStarted(false);
                setMessages([]);
                setScheduledAppointment(null);
              }}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${info.color} flex items-center justify-center text-xl`}>
                {info.icon}
              </div>
              <div>
                <div className="font-bold">{companyName}</div>
                <div className="text-xs text-slate-400 flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  AI Chat Demo - {info.name}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 max-w-2xl w-full mx-auto px-4 sm:px-6 py-6 overflow-y-auto">
        <div className="space-y-4">
          {messages.map((message, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                message.role === 'user' 
                  ? 'bg-sky-500' 
                  : `bg-gradient-to-br ${info.color}`
              }`}>
                {message.role === 'user' ? (
                  <User className="w-4 h-4" />
                ) : (
                  <Bot className="w-4 h-4" />
                )}
              </div>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                message.role === 'user'
                  ? 'bg-sky-500 text-white'
                  : 'bg-slate-800 text-slate-200'
              }`}>
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              </div>
            </motion.div>
          ))}
          
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex gap-3"
            >
              <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${info.color} flex items-center justify-center`}>
                <Bot className="w-4 h-4" />
              </div>
              <div className="bg-slate-800 rounded-2xl px-4 py-3">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </motion.div>
          )}
          
          {/* Scheduled Appointment Card */}
          <AnimatePresence>
            {scheduledAppointment && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mt-4"
              >
                <div className="p-4 rounded-xl border border-green-500/30 bg-gradient-to-r from-green-500/10 to-emerald-500/10">
                  <div className="flex items-center gap-2 text-green-400 mb-3">
                    <Calendar className="w-5 h-5" />
                    <span className="font-semibold">Appointment Scheduled!</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-300">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span>{scheduledAppointment.date} at {scheduledAppointment.time}</span>
                    </div>
                    {scheduledAppointment.service && (
                      <p className="text-slate-400">Service: {scheduledAppointment.service}</p>
                    )}
                    <p className="text-xs text-slate-500 mt-2 italic">
                      (This is a demo - no real appointment was created)
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Replies */}
        {messages.length <= 2 && (
          <div className="mt-6">
            <p className="text-xs text-slate-500 mb-2">Quick replies:</p>
            <div className="flex flex-wrap gap-2">
              {quickReplies.map((reply, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setInput(reply);
                    setTimeout(() => sendMessage(), 100);
                  }}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-full text-sm text-slate-300 transition-colors"
                  data-testid={`quick-reply-${index}`}
                >
                  {reply}
                </button>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Input */}
      <div className="border-t border-slate-800 bg-slate-900/50">
        <form onSubmit={sendMessage} className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex gap-3">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              disabled={isLoading}
              className="flex-1 bg-slate-800 border-slate-700 text-white"
              data-testid="chat-input"
            />
            <Button 
              type="submit" 
              disabled={isLoading || !input.trim()}
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="chat-send-btn"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-slate-600 mt-2 text-center">
            This is a demo. No actual appointments will be scheduled.
          </p>
        </form>
      </div>
    </div>
  );
}
